import { useState } from "react";
import { Award, Download, Loader2, Share2 } from "lucide-react";
import { toast } from "sonner";
import { useApi, Skeleton } from "@/components/portal/ui";
import { monthLabel } from "@/pages/portal/Leaderboard";

const W = 1080, H = 1920;

function drawPiagam(a) {
  const c = document.createElement("canvas");
  c.width = W; c.height = H;
  const g = c.getContext("2d");
  const bg = g.createLinearGradient(0, 0, W, H);
  bg.addColorStop(0, "#020617"); bg.addColorStop(0.55, "#0b1730"); bg.addColorStop(1, "#020617");
  g.fillStyle = bg; g.fillRect(0, 0, W, H);
  g.strokeStyle = "rgba(251,191,36,0.55)"; g.lineWidth = 6;
  g.strokeRect(60, 60, W - 120, H - 120);
  g.strokeStyle = "rgba(255,255,255,0.10)"; g.lineWidth = 2;
  g.strokeRect(92, 92, W - 184, H - 184);

  const center = (text, y, font, color, spacing = 0) => {
    g.font = font; g.fillStyle = color; g.textAlign = "center";
    if (!spacing) { g.fillText(text, W / 2, y); return; }
    const chars = [...text];
    const width = chars.reduce((s, ch) => s + g.measureText(ch).width + spacing, 0) - spacing;
    let x = W / 2 - width / 2;
    g.textAlign = "left";
    chars.forEach((ch) => { g.fillText(ch, x, y); x += g.measureText(ch).width + spacing; });
    g.textAlign = "center";
  };

  center("GIM CHURCH MAKASSAR", 250, "700 38px Georgia, serif", "#93c5fd", 8);
  center("PIAGAM DIGITAL PELAYAN", 340, "800 56px Georgia, serif", "#fbbf24", 4);
  center("diberikan kepada", 520, "400 40px Georgia, serif", "rgba(255,255,255,0.6)");

  const name = a.name.length > 22 ? a.name.slice(0, 21) + "…" : a.name;
  center(name, 660, "800 92px Georgia, serif", "#ffffff");
  g.strokeStyle = "rgba(251,191,36,0.6)"; g.lineWidth = 4;
  g.beginPath(); g.moveTo(W / 2 - 240, 700); g.lineTo(W / 2 + 240, 700); g.stroke();

  center(a.title.toUpperCase(), 800, "800 52px Georgia, serif", "#fbbf24", 2);
  center(`Bulan ${monthLabel(a.month)}`, 870, "400 40px Georgia, serif", "rgba(255,255,255,0.7)");

  g.fillStyle = "rgba(255,255,255,0.06)";
  g.fillRect(140, 960, W - 280, 300);
  center(`${a.served}x`, 1090, "800 140px Georgia, serif", "#60a5fa");
  center("kehadiran pelayanan", 1160, "400 40px Georgia, serif", "rgba(255,255,255,0.65)");
  center(`${a.rate}% dari ${a.assigned} jadwal · ${(a.roles || []).join(" · ") || "Pelayan"}`, 1225, "400 32px Georgia, serif", "rgba(255,255,255,0.45)");

  const verse = ["\u201CBarangsiapa melayani, ia harus melakukannya", "dengan kekuatan yang dianugerahkan Allah.\u201D"];
  verse.forEach((line, i) => center(line, 1420 + i * 56, "italic 400 36px Georgia, serif", "rgba(255,255,255,0.55)"));
  center("1 Petrus 4:11", 1540, "700 32px Georgia, serif", "#93c5fd");

  center("Gembala Sidang · GIM Church Makassar", 1700, "700 34px Georgia, serif", "rgba(255,255,255,0.75)");
  center(`No. ${a.number}`, 1760, "400 28px Georgia, serif", "rgba(255,255,255,0.35)");
  return c;
}

function AwardCard({ a }) {
  const [busy, setBusy] = useState(false);

  const blob = async () => {
    const canvas = drawPiagam(a);
    return new Promise((res) => canvas.toBlob(res, "image/png"));
  };
  const download = async () => {
    setBusy(true);
    try {
      const b = await blob();
      const url = URL.createObjectURL(b);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Piagam-${a.number.replace(/\//g, "-")}.png`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success("Piagam tersimpan. Siap dibagikan ke status WA!");
    } finally {
      setBusy(false);
    }
  };
  const share = async () => {
    setBusy(true);
    try {
      const b = await blob();
      const file = new File([b], `Piagam-${a.number.replace(/\//g, "-")}.png`, { type: "image/png" });
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title: a.title, text: `${a.title} · ${monthLabel(a.month)} · GIM Church Makassar` });
      } else {
        await download();
      }
    } catch {
      /* dibatalkan pengguna */
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-3xl bg-slate-900 text-white p-5 border border-amber-400/30" data-testid={`award-${a.id}`}>
      <div className="flex items-start gap-3">
        <span className="w-11 h-11 shrink-0 rounded-2xl bg-amber-400/15 text-amber-300 grid place-items-center"><Award className="w-6 h-6" /></span>
        <div className="min-w-0">
          <p className="font-extrabold leading-tight">{a.title}</p>
          <p className="text-xs text-white/60 mt-0.5">{monthLabel(a.month)} · {a.served}x melayani · {a.rate}% hadir</p>
          <p className="text-[11px] text-white/35 mt-1">No. {a.number}</p>
        </div>
      </div>
      <div className="mt-4 flex gap-2">
        <button onClick={share} disabled={busy} data-testid={`award-share-${a.id}`} className="flex-1 inline-flex items-center justify-center gap-2 bg-amber-400 text-slate-900 rounded-xl py-2.5 text-sm font-bold active:scale-95 transition disabled:opacity-60">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Share2 className="w-4 h-4" />Bagikan ke Status</>}
        </button>
        <button onClick={download} disabled={busy} data-testid={`award-download-${a.id}`} className="inline-flex items-center justify-center gap-2 bg-white/10 rounded-xl px-4 text-sm font-bold active:scale-95 transition"><Download className="w-4 h-4" /></button>
      </div>
    </div>
  );
}

export default function MyAwards() {
  const { data } = useApi("/portal/ministry/awards");
  if (!data) return <Skeleton className="h-24" />;
  if (data.length === 0) return null;
  return (
    <div className="space-y-3" data-testid="my-awards">
      <p className="px-1 text-sm font-bold text-slate-900">Piagam Digital Pelayan</p>
      {data.map((a) => <AwardCard key={a.id} a={a} />)}
    </div>
  );
}
